import React from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';

const Buttons = ({ bookingId, trackAnalyticFunc }) => (
  <div className="referral-btns flex">
    <a href={`/inbox/${bookingId}`} className="btn btn-default skip-btn">
      <FormattedMessage id='referrals.buttons.skip_for_now' defaultMessage="Skip for Now" />
    </a>
    <button className="btn btn-primary" onClick={trackAnalyticFunc} id="referral_btn">
      <FormattedMessage id='referrals.buttons.get_link_btn' defaultMessage="Get Referral Link" />
    </button>
  </div>
);

const ReferralsMain = ({
  entitlementDescription,
  creditValue,
  discountValue,
  bookingId,
  intl
}) => {
  const howItWorksData = [
    {
      icons: ['instagram', 'youtube', 'tiktok'],
      header: intl.formatMessage({ id: 'referrals.referrals_link_main.share_anywhere', defaultMessage: 'Share anywhere' }),
      body: intl.formatMessage({ id: 'referrals.referrals_link_main.creative_you_share', defaultMessage: 'Get creative with how you share. Text your link to friends, create an Instagram story, or shout it out on TikTok.' })
    },
    {
      icons: ['dollar'],
      header: intl.formatMessage({ id: 'referrals.referrals_link_main.earn_up', defaultMessage: 'Earn up to {value}' }, { value: intl.formatNumber(5000, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === 5000 % 1 ? 0 : 2, maximumFractionDigits: 0 === 5000 % 1 ? 0 : 2 }) }),
      body: intl.formatMessage({ id: 'referrals.referrals_link_main.stack_credits', defaultMessage: 'Stack those credits. You can earn up to {value} through your referral link.' }, { value: intl.formatNumber(5000, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 === 5000 % 1 ? 0 : 2, maximumFractionDigits: 0 === 5000 % 1 ? 0 : 2 }) }),
      link: true
    },
    {
      icons: ['calendar'],
      header: intl.formatMessage({ id: 'referrals.referrals_link_main.any_booking', defaultMessage: 'Good for any US booking' }),
      body: intl.formatMessage({ id: 'referrals.referrals_link_main.use_peerspace_credit', defaultMessage: 'Use Peerspace Credit to book any space in the United States.' }),
      link: true
    }
  ];
  const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();

  const onReferralBtnClick = () => {
    analyticsIntegrations.trackReferralClick('promo page');
    window.location.href = '/referrals';
  };

  return (
    <div className='referrals-promotion-main'>
      <div className='ps-container-lg-margin promotion-top-container referral-promotion-image'>
        <div className='ps-row'>
          <div className='col-xs-12 col-md-5'>
            <h1 className='h1'>{entitlementDescription}</h1>
            <div className='text-gray-800 margin-xl-bottom'>
              <FormattedMessage
                id='referrals.referrals_link_main.share_link'
                defaultMessage="Share your referral link to give a {discount} discount to friends and followers. When they book their first space, you'll earn a {credit} credit."
                values={{
                  discount: intl.formatNumber(discountValue, {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0 === discountValue % 1 ? 0 : 2,
                    maximumFractionDigits: 0 === discountValue % 1 ? 0 : 2
                  }),
                  credit: intl.formatNumber(creditValue, {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0 === creditValue % 1 ? 0 : 2,
                    maximumFractionDigits: 0 === creditValue % 1 ? 0 : 2
                  })
                }}
              />
            </div>
            <div>
              <Buttons
                bookingId={bookingId}
                trackAnalyticFunc={onReferralBtnClick}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='ps-container-lg-margin referral-details-wrap'>
        <div className='ps-row'>
          {map(howItWorksData, (section) => {
            const { icons, header, body, link } = section;
            return (
              <div className='referral-details col-xs-12 col-md-4 text-center'>
                <div>
                  {map(icons, (i) => {
                    return (
                      <img
                        src={`https://assets.peerspace.com/${i}-gray.svg?v=rb`}
                        className='margin-md-left margin-md-right'
                        alt={`${i}-icon`}
                      />
                    );
                  })}
                </div>
                <div className='h5 margin-xl-top'>{header}</div>
                <div className='text-gray-600 margin-md-bottom margin-lg-top'>
                  {body}
                </div>
                {link && (
                  <a
                    href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/360057498172-How-does-the-guest-referral-program-work-`}
                    target='_blank'
                  >
                    <FormattedMessage
                      id='referrals.referrals_link_main.learn_more'
                      defaultMessage='Learn More ›'
                    />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

ReferralsMain.propTypes = {
  referralCount: PropTypes.number,
  referralBalanceAmount: PropTypes.number,
  referralBalanceError: PropTypes.string,
  entitlementId: PropTypes.string,
  entitlementError: PropTypes.object,
  SSOid: PropTypes.string,
  entitlementDescription: PropTypes.string,
  creditValue: PropTypes.string,
  discountValue: PropTypes.number,
  minimumBookingSubtotal: PropTypes.number,
  bookingId: PropTypes.string,
  intl: intlShape
};

Buttons.propTypes = {
  bookingId: PropTypes.string,
};

export default injectIntl(ReferralsMain);
